import React from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { logPDFDownload } from '../services/analytics';

const SolutionDisplay = ({ solution, isLoading, uploadedImage }) => {
    const handleDownloadPDF = async () => {
        const element = document.getElementById('solution-content');
        if (!element) return;

        try {
            // Capture the solution content as canvas
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff'
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
            const imgX = (pdfWidth - imgWidth * ratio) / 2;
            const imgY = 10;

            pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
            pdf.save('math-solution.pdf');

            // Log PDF download event
            logPDFDownload();
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to generate PDF. Please try again.');
        }
    };

    if (!uploadedImage && !isLoading && !solution) {
        return (
            <div className="w-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-12 text-center border-2 border-dashed border-gray-300">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Solution Yet</h3>
                        <p className="text-gray-500">Upload an image to get started</p>
                    </div>
                </div>
            </div>
        );
    }

    // Show waiting state when image is uploaded but no solution yet
    if (uploadedImage && !solution && !isLoading) {
        return (
            <div className="w-full bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-12 text-center border-2 border-indigo-200">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-indigo-900 mb-2">Ready to Solve!</h3>
                        <p className="text-indigo-700">Click "Find Solution" to analyze your math problem</p>
                    </div>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="w-full bg-white rounded-2xl shadow-xl p-12 border-2 border-indigo-100">
                <div className="flex flex-col items-center gap-6">
                    <div className="relative">
                        <div className="w-20 h-20 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                        </div>
                    </div>
                    <div className="text-center">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Analyzing Your Problem...</h3>
                        <p className="text-gray-600">Our AI is working on the solution</p>
                    </div>
                    <div className="flex gap-2">
                        <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                </div>
            </div>
        );
    }

    // Only show solution if it actually exists
    if (!solution) {
        return null;
    }

    return (
        <div className="w-full">
            <div id="solution-content" className="bg-white rounded-2xl shadow-xl p-8 border-2 border-green-100">
                {/* Success Header */}
                <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-200">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-gray-900">Solution Ready!</h3>
                        <p className="text-sm text-gray-600">Step-by-step solution below</p>
                    </div>
                </div>

                {/* Solution Content - Placeholder for API integration */}
                <div className="space-y-6">
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                        <p className="text-sm text-blue-800 font-semibold mb-1">📝 Problem Identified:</p>
                        <p className="text-blue-900">
                            {solution?.problem || "Your math problem has been analyzed successfully."}
                        </p>
                    </div>

                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm">✓</span>
                            Step-by-Step Solution:
                        </h4>

                        {solution?.steps ? (
                            solution.steps.map((step, index) => (
                                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div className="flex gap-3">
                                        <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">
                                            {index + 1}
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-gray-900">{step}</p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="space-y-3">
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div className="flex gap-3">
                                        <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                                        <p className="text-gray-900">API integration pending - Solution will appear here</p>
                                    </div>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div className="flex gap-3">
                                        <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                                        <p className="text-gray-900">Each step will be clearly explained</p>
                                    </div>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div className="flex gap-3">
                                        <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                                        <p className="text-gray-900">Final answer will be highlighted</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
                        <p className="text-sm text-green-800 font-semibold mb-1">✅ Final Answer:</p>
                        <p className="text-xl font-bold text-green-900">
                            {solution?.answer || "Answer will be displayed here"}
                        </p>
                    </div>
                </div>
            </div>

            {/* Download Button */}
            <div className="mt-6 flex justify-center">
                <button
                    onClick={handleDownloadPDF}
                    className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download as PDF
                </button>
            </div>
        </div>
    );
};

export default SolutionDisplay;
